import { Component, OnDestroy, OnInit, VERSION } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PaginationHelperService } from 'src/app/services/pagination-helper.service';

import { DebounceService } from 'src/app/core/services/debounce.service';
import { ToastrService } from 'ngx-toastr';
import { DialogService } from 'src/app/core/services/dialog.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ApiService } from 'src/app/services/api.service';
import { SkuService } from '../sku.service';
import { CategoryService } from 'src/app/shared/component/custom-category/category.service';
import { MatDialog } from '@angular/material/dialog';
import { CreateEditSkuModalComponent } from './create-edit-sku-modal/create-edit-sku-modal.component';

import { SKU_TAB_OPTIONS } from 'src/app/lib/constants/tabs.constants';
import { SKU_TABLE_HEADERS, SKU_TABLE_SUB_HEADERS } from 'src/app/lib/constants/sku.constants';
import { RbacService } from 'src/app/services/rbac.service';
import { Permission } from 'src/app/lib/rbac.config';

@Component({
  selector: 'app-sku-biller-all-transaction',
  templateUrl: './sku-biller-all-transaction.component.html',
  styleUrls: ['./sku-biller-all-transaction.component.scss']
})
export class SkuBillerAllTransactionComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  // ─── Provider Options ──────────────────────────────────────────────────────

  providerOptions = [
    { value: 0, label: '<strong>Default</strong> Resound for Globe, Digilink for Smart/Sun/TNT' },
    { value: 1, label: '<strong>Digilink</strong> Digilink for all networks' },
    { value: 2, label: '<strong>Resound</strong> Resound for all networks' },
  ];

  // ─── Forms ─────────────────────────────────────────────────────────────────

  searchForm!: UntypedFormGroup;
  limitForm!: UntypedFormGroup;

  // ─── Table ─────────────────────────────────────────────────────────────────

  tabs           = SKU_TAB_OPTIONS;
  tableHeader    = SKU_TABLE_HEADERS;
  tableSubHeader = SKU_TABLE_SUB_HEADERS;
  buyLoad_lists: any;
  isLoading      = false;

  // ─── Provider Switcher State ───────────────────────────────────────────────

  selectedProvider = 0;
  provider_text: any;

  // ─── Pagination ────────────────────────────────────────────────────────────

  size         = 10;
  total_pages: any;
  current_page = 1;
  pages_array: number[] = [];
  total: any;

  // ─── Search & Limit ────────────────────────────────────────────────────────

  searchKey: any = null;
  search_keyword = '';
  limitKey: any  = null;

  // ─── Sorting ───────────────────────────────────────────────────────────────

  sort_column        = 'dateTime';
  sort_type: 'ASC' | 'DESC' = 'ASC';

  // ─── Misc ──────────────────────────────────────────────────────────────────

  params = { search: '' };

  constructor(
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private dialogService: DialogService,
    private apiService: ApiService,
    private skuTransactionsService: SkuService,
    public debounce: DebounceService,
    public paginationHelper: PaginationHelperService,
    private categoryService: CategoryService,
    private dialog: MatDialog,
    private rbac: RbacService,
  ) {
    this.initializeForm();
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.tabs = SKU_TAB_OPTIONS.filter(tab => !tab['permission'] || this.rbac.hasPermission(tab['permission'] as Permission));
    this.get_LoadProvider_list();
    this.get_buyLoad_list();

    this.categoryService.categoryUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.get_buyLoad_list());

    const tableHeader = document.getElementsByTagName('th');
    for (let i = 0; i < tableHeader.length; i++) {
      tableHeader[i].classList.add('headerSortDown');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Form Initialization ───────────────────────────────────────────────────

  initializeForm(): void {
    this.searchForm = this.fb.group({ search: [''] });
    this.limitForm  = this.fb.group({ limit:  [''] });

    this.searchKey = this.debounce.debounce(() => {
      this.current_page   = 1;
      this.search_keyword = this.searchForm.value.search;
      this.get_buyLoad_list();
    }, 500);

    this.limitKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.size         = this.limitForm.value.limit;
      this.get_buyLoad_list();
    }, 500);
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────

  private handleBuyloadError(error: any): void {
    console.error(error);
    this.toastr.error(
      'Please refresh or contact the developer if the error still occurs.',
      'Failed Fetching Buyload List'
    );
  }

  // ─── Load Provider ─────────────────────────────────────────────────────────

  get_LoadProvider_list(): void {
    this.apiService.getLoadProviderList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.result) {
            const active = response.result.find((p: any) => p.status === 0);
            if (active !== undefined) {
              this.selectedProvider = active.uid;
              const matched = this.providerOptions.find(opt => opt.value === active.uid);
              if (matched) { this.provider_text = matched.label; }
            }
          }
        },
        error: (error) => {throw error(`Error fetching load provider list: ${error.message || error}`)},
      });
  }

  setProvider(text: string, value: any): void {
    this.selectedProvider = value;
    this.provider_text    = text;
  }

  async switchLoadProvider(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.apiService.switchLoadProvider(this.selectedProvider, { status: this.selectedProvider })
      );

      if (response.status === 200) {
        this.toastr.success(response.message + '!', 'Success!');
      } else {
        this.toastr.error(response.error?.message + '!', 'Something went wrong!');
      }

      this.get_LoadProvider_list();
    } catch (error: any) {
      this.toastr.error(error.message + '!', 'Something went wrong!');
    }
  }

  // ─── Buy Load List ─────────────────────────────────────────────────────────

  get_buyLoad_list(): void {
    this.spinner.show().catch(console.error);
    this.pages_array = [];

    const params = {
      search: this.search_keyword,
      sortBy: { [this.sort_column]: this.sort_type },
    };

    this.apiService.getBuyLoadItems(this.current_page, this.size, params).subscribe({
      next: (response: any) => {
        if (response.status === 200) {
          this.buyLoad_lists = response.result.data;
          this.isLoading     = false;
          this.pages_array   = this.paginationHelper.generatePageArray(this.current_page, response.result.totalPages);
          this.total_pages   = parseInt(response.result.totalPages, 10);
          this.current_page  = response.result.currentPage;
          this.total         = parseInt(response.result.totalItems, 10);
        }
      },
      error: (err: any) => this.handleBuyloadError(err),
      complete: () => this.spinner.hide().catch(console.error),
    });
  }

  // ─── Table Controls ────────────────────────────────────────────────────────

  onLimitChangeFromComponent(event: any): void {
    this.limitForm.patchValue({ limit: event.target.value });
    this.limitKey();
  }

  onSearchKeyupFromComponent(event: any): void {
    this.searchForm.patchValue({ search: event.target.value });
    this.searchKey();
  }

  keyDownFunction(event: any): void {
    if (event.keyCode === 13) { this.searchList(); }
  }

  searchList(): void {
    this.current_page   = 1;
    this.search_keyword = this.searchForm.value.search;
    this.get_buyLoad_list();
  }

  sortList(sort_column: string, sort_type: 'ASC' | 'DESC'): void {
    this.spinner.show().catch(console.error);
    const { column, type } = this.paginationHelper.toggleSort(this.sort_column, this.sort_type, sort_column, sort_type);
    this.sort_column = column;
    this.sort_type   = type;
    if (this.buyLoad_lists) { this.get_buyLoad_list(); }
  }

  // ─── Pagination ────────────────────────────────────────────────────────────

  setPage(num: number): void {
    this.current_page = num;
    this.get_buyLoad_list();
  }

  nextPage(): void {
    if (this.current_page < Number(this.total_pages)) {
      this.current_page++;
      this.get_buyLoad_list();
    }
  }

  prevPage(): void {
    if (this.current_page > 1) {
      this.current_page--;
      this.get_buyLoad_list();
    }
  }

  // ─── Enable / Disable ──────────────────────────────────────────────────────

  disable(id: number, value: number): void {
    this.skuTransactionsService.toggleStatus(id, value, 'disable', () => this.get_buyLoad_list());
  }

  enable(id: number, value: number): void {
    this.skuTransactionsService.toggleStatus(id, value, 'enable', () => this.get_buyLoad_list());
  }

  // ─── Dialogs ───────────────────────────────────────────────────────────────

  openCategoryDialog(): void {
    this.dialogService.openAddCategoryDialog()
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  openEditDialog(uid: string): void {
    this.dialog.open(CreateEditSkuModalComponent, {
      width: '75vw',
      maxHeight: '90vh',
      height: '570px',
      panelClass: 'create-edit-sku-dialog',
      data: { mode: 'edit', item: { uid } },
      disableClose: true,
    }).afterClosed().subscribe((saved: any) => { if (saved) { this.get_buyLoad_list(); } });
  }

  openCreateDialog(): void {
    this.dialog.open(CreateEditSkuModalComponent, {
      width: '75vw',
      maxHeight: '90vh',
      height: '450px',
      panelClass: 'create-edit-sku-dialog',
      data: { mode: 'create' },
      disableClose: true,
    }).afterClosed().subscribe((saved: any) => { if (saved) { this.get_buyLoad_list(); } });
  }

  // ─── Export ────────────────────────────────────────────────────────────────

  async print(): Promise<void> {
    this.spinner.show().catch(console.error);

    const params = {
      search: this.search_keyword,
      sortBy: { [this.sort_column]: this.sort_type },
    };

    try {
      this.apiService.exportBuyloadItems(params).subscribe((blob: Blob) => {
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        a.href = objectUrl;
        a.download = 'buyload_items.xlsx';
        a.click();
        URL.revokeObjectURL(objectUrl);
      });
    } catch (err) {
      this.toastr.error('Failed to export buy load items.', 'Export Error');
    } finally {
      this.spinner.hide().catch(console.error);
    }
  }
}
